// src/services/chatbotApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions';

export interface ChatbotMessage {
  _id: string;
  businessId: string;
  clientId: string;
  userId?: string;
  sender: 'user' | 'bot';
  content: string;
  suggestions?: { id: string; text: string }[];
  metadata?: Record<string, any>;
  sessionId: string;
  createdAt: string;
  updatedAt: string;
}

export interface SendMessageRequest {
  userId?: string;
  message: string;
  sessionId?: string;
  context?: Record<string, any>;
}

export interface SendMessageResponse {
  text: string;
  suggestions?: { id: string; text: string }[];
  sessionId: string;
  success: boolean;
  metadata?: {
    sourceId?: string;
    knowledgeUsed?: boolean;
    responseSource?: string;
    [key: string]: any;
  };
}

export interface GetHistoryParams {
  businessId: string;
  userId?: string;
  sessionId?: string;
  limit?: number;
  page?: number;
}

export interface GetHistoryResponse {
  messages: ChatbotMessage[];
  total: number;
  page: number;
  limit: number;
  success: boolean;
}

export interface ClearHistoryResponse {
  success: boolean;
  deletedCount: number;
}

export interface GetSessionsParams {
  businessId: string;
  limit?: number;
  page?: number;
}

export interface ChatSession {
  sessionId: string;
  lastMessage: string;
  lastMessageTime: string;
  messageCount: number;
  user: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export interface GetSessionsResponse {
  sessions: ChatSession[];
  total: number;
  page: number;
  limit: number;
  success: boolean;
}

// Feedback interfaces
export interface SendFeedbackRequest {
  wasHelpful: boolean;
  feedbackText?: string;
}

export interface SendFeedbackResponse {
  success: boolean;
}

export const chatbotApi = createApi({
  reducerPath: 'chatbotApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business-chatbot`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['ChatHistory', 'ChatSessions'],
  endpoints: (builder) => ({
    sendChatMessage: builder.mutation<SendMessageResponse, { businessId: string } & SendMessageRequest>({
      query: ({ businessId, ...data }) => ({
        url: `/${businessId}/message`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['ChatHistory']
    }),
    
    getChatHistory: builder.query<GetHistoryResponse, GetHistoryParams>({
      query: ({ businessId, userId, sessionId, limit, page }) => {
        let url = `/${businessId}/history`;
        const params = new URLSearchParams();
        
        if (userId) params.append('userId', userId);
        if (sessionId) params.append('sessionId', sessionId);
        if (limit) params.append('limit', limit.toString());
        if (page) params.append('page', page.toString());
        
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
        
        return url;
      },
      providesTags: ['ChatHistory']
    }),
    
    clearChatHistory: builder.mutation<ClearHistoryResponse, { businessId: string, sessionId: string }>({
      query: ({ businessId, sessionId }) => ({
        url: `/${businessId}/history?sessionId=${sessionId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ChatHistory', 'ChatSessions']
    }),
    
    getChatSessions: builder.query<GetSessionsResponse, GetSessionsParams>({
      query: ({ businessId, limit, page }) => {
        let url = `/${businessId}/sessions`;
        const params = new URLSearchParams();
        
        if (limit) params.append('limit', limit.toString());
        if (page) params.append('page', page.toString());
        
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
        
        return url;
      },
      providesTags: ['ChatSessions']
    }),

    sendFeedback: builder.mutation<{success: boolean}, { 
        businessId: string,
        sourceId?: string,
        sourceType?: string,
        wasHelpful: boolean, 
        messageId: string,
        sessionId?: string,
        feedbackText?: string
      }>({
        query: ({ businessId, ...data }) => ({
          url: `/${businessId}/feedback`,
          method: 'PUT',
          body: data
        })
      }),

    sendQueryFeedback: builder.mutation<any, { 
        id: string, 
        wasSuccessful: boolean 
      }>({
        query: ({ id, wasSuccessful }) => ({
          url: `query-responses/${id}/feedback`,
          method: 'PUT',
          body: { wasSuccessful }
        })
      })
  }),
  
});

export const {
  useSendChatMessageMutation,
  useGetChatHistoryQuery,
  useClearChatHistoryMutation,
  useGetChatSessionsQuery,
  useSendQueryFeedbackMutation,
  useSendFeedbackMutation,
  useLazyGetChatHistoryQuery
} = chatbotApi;